import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import { useApolloClient } from '@apollo/react-hooks';
import { ActivatePaymentMethodV2 as activatePaymentMethodMutation } from 'src/payments/graphql/mutations/activate-payment-method.gql';
import { useObserver } from 'mobx-react';
import { EnrollmentSteps, LOG_NAMESPACES } from 'src/payments/constants';
import ShowEnrollment from 'src/payments/graphql/queries/show-enrollment.gql';
import {
  ENROLLMENT_EVENTS,
  EVENT_ACTIONS,
  useDutchiePayAnalytics,
} from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';
import useErnie from 'shared/hooks/use-ernie';
import { formatPaymentsError } from 'src/payments/hooks/use-payments-error/use-payments-error';
import { ERNIE_TYPES } from 'shared/constants';
import { getLogger } from 'src/utils/datadog-logger';
import { useDutchiePay } from 'src/payments/hooks';
import useUser from 'hooks/use-user';
import { GqlShowEnrollmentQuery } from 'types/graphql';

const logger = getLogger(LOG_NAMESPACES.PBB_ENROLLMENT);

type ActivatePaymentMethodResult = {
  activatePaymentMethod: () => Promise<void>;
};

export const useActivatePaymentMethod = (): ActivatePaymentMethodResult => {
  const apolloClient = useApolloClient();
  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const showErnie = useErnie();

  const connectedBankAccountDetails = useObserver(() => DutchiePayEnrollment.connectedBankAccountDetails);
  const { dutchiePayEventTracker, trackEnrollmentStepViewed } = useDutchiePayAnalytics();
  const { whiteLabelConfig } = useDutchiePay();
  const User = useUser();

  const activatePaymentMethod = async (): Promise<void> => {
    DutchiePayEnrollment.activateBankAccountRequested();
    const activateBankAccountProps = {
      paymentMethodId: connectedBankAccountDetails.id,
    };

    try {
      const { data: activatePaymentMethodData } = await apolloClient.mutate({
        mutation: activatePaymentMethodMutation,
        variables: activateBankAccountProps,
      });

      const { data: showEnrollmentData } = await apolloClient.query<GqlShowEnrollmentQuery>({
        query: ShowEnrollment,
        fetchPolicy: 'no-cache',
      });

      const success = activatePaymentMethodData?.activatePaymentMethodV2?.genericResponse.success;
      const { enrollmentStatus, paymentMethods } = showEnrollmentData.showEnrollment;

      const validPaymentMethods = paymentMethods.filter(
        (paymentMethod) => !!paymentMethod && paymentMethod.status === 'active'
      );

      if (success && validPaymentMethods.length > 0) {
        // This has a side effect of updating the DutchiePayEnrollment store step to COMPLETE ENROLLMENT
        User.setUserPayByBankEnrollmentStatus({
          enrollmentStatus,
          paymentMethods: validPaymentMethods,
          completeEnrollment: true,
          relinkOrEditBankAccountEnabled: false,
        });
        showErnie(`Your account information has been saved!`, ERNIE_TYPES.SUCCESS);
        trackEnrollmentStepViewed(EnrollmentSteps.completeEnrollment);
      } else {
        throw Object.assign(new Error('Failed to activate payment method'), {
          ernieMessage: 'Pay by Bank is unavailable. Please use a different method of payment.',
        });
      }
    } catch (err) {
      DutchiePayEnrollment.termsOfServiceError();

      const { errorMarkdown, ernieType, ernieTimeout } = formatPaymentsError(
        err,
        whiteLabelConfig.brandedDutchiePayName
      );
      const ernieMessage =
        err.ernieMessage ?? errorMarkdown ?? 'We are unable to activate your payment method. Please contact support.';
      showErnie(ernieMessage, ernieType, ernieTimeout);

      dutchiePayEventTracker(ENROLLMENT_EVENTS.ENROLLMENT_STEP, {
        step: EnrollmentSteps.terms,
        action: EVENT_ACTIONS.FAILED,
      });

      logger.error('Payment method activation error', {
        error: err.message ?? 'Unknown error',
        errorCode: err.graphQLErrors?.[0]?.extensions?.reason_code ?? 'NO_CODE',
        step: 'terms',
        paymentMethodId: connectedBankAccountDetails?.id ?? 'unknown',
        dispensaryId: DutchiePayEnrollment.dispensary.id ?? 'unknown',
        userId: User.id ?? 'unknown',
      });
      console.error(err);
    }
  };

  return {
    activatePaymentMethod,
  };
};
